import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Flame, ArrowRight, Mail, MessageCircle, Phone, Linkedin } from 'lucide-react';
import api from '../../lib/api';

const TONE_STYLES = {
  critical: 'border-l-red-500 bg-red-500/[0.04] hover:bg-red-500/[0.08]',
  high: 'border-l-amber-500 bg-amber-500/[0.04] hover:bg-amber-500/[0.08]',
  medium: 'border-l-blue-500 bg-blue-500/[0.04] hover:bg-blue-500/[0.08]',
  low: 'border-l-muted-foreground/40 hover:bg-muted/50',
};

const TONE_BADGE = {
  critical: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  high: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  medium: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  low: 'bg-muted text-muted-foreground',
};

const CHANNEL_ICONS = {
  email: Mail,
  whatsapp: MessageCircle,
  call: Phone,
  voice: Phone,
  linkedin: Linkedin,
};

function formatTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  const diff = (Date.now() - d) / 1000;
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export default function HotRepliesWidget() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = () => {
    const token = localStorage.getItem('access_token');
    if (!token) { setLoading(false); return; }
    api.get('/api/brain/notifications/hot-replies?limit=8')
      .then(r => {
        if (r.data) {
          setItems(r.data.hot_replies || []);
          setTotal(r.data.total || 0);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60000); // refresh every 60s for fresh triage
    return () => clearInterval(id);
  }, []);

  if (loading) {
    return (
      <Card data-testid="hot-replies-widget-loading">
        <CardContent className="p-6">
          <div className="h-24 flex items-center justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!items.length) return null; // Hide when no replies to triage

  return (
    <Card data-testid="hot-replies-widget" className="border-red-500/20">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Flame className="h-5 w-5 text-red-500" />
            Hot Replies
            <Badge variant="secondary" className="text-xs" data-testid="hot-replies-count">
              {total}
            </Badge>
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs gap-1"
            onClick={() => navigate('/leads')}
            data-testid="hot-replies-view-all"
          >
            View Leads <ArrowRight className="h-3 w-3" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0 space-y-1.5" data-testid="hot-replies-list">
        {items.map((it) => {
          const ChIcon = CHANNEL_ICONS[it.channel] || Mail;
          return (
            <div
              key={it.lead_id}
              className={`flex items-center gap-3 p-2.5 rounded-lg border-l-2 cursor-pointer transition-colors ${TONE_STYLES[it.tone] || TONE_STYLES.low}`}
              onClick={() => navigate(`/leads/${it.lead_id}`)}
              data-testid={`hot-reply-${it.lead_id}`}
            >
              <span className="text-lg shrink-0 leading-none" aria-hidden>{it.intent_emoji}</span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium leading-tight truncate">
                  {it.name}
                  {it.company && <span className="text-muted-foreground font-normal"> · {it.company}</span>}
                </p>
                <div className="flex items-center gap-1.5 mt-0.5 text-xs text-muted-foreground">
                  <ChIcon className="h-3 w-3" />
                  <span className="capitalize">{it.channel}</span>
                  <span>·</span>
                  <span>{formatTime(it.replied_at)}</span>
                </div>
              </div>
              <Badge className={`text-[10px] shrink-0 ${TONE_BADGE[it.tone] || TONE_BADGE.low}`} data-testid={`hot-reply-intent-${it.lead_id}`}>
                {it.intent_label}
              </Badge>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
